import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Upload, FileText, Trash2, CheckCircle, XCircle, AlertTriangle,
  Loader2, RotateCcw, Clock, ChevronDown, ChevronUp, ArrowUpCircle, History,
  FolderOpen, ListChecks, Ban, ScanLine,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Button, Badge, ConfirmModal, IconButton, Input, Select, Tag, ToolbarChip, EmptyState, SkeletonTable,
} from '../ui';
import type { TagTone } from '../ui';
import { money } from '../../lib/money';
import {
  useStatements, useDeleteStatement, uploadFile, detectBundle, getBundle, useCaseJobs, useRetryJob,
  useDeleteJob, useDeleteAllFailedJobs, useForceResetJob, useJobProgress,
  useIntakeProposals, useConfirmProposal, useDismissProposal, useReclassifyProposal,
} from '../../hooks/useStatements';
import type { SourceParty, DetectedBoundary, BatesRangeInput } from '../../hooks/useStatements';
import { useCase } from '../../hooks/useCases';
import { useFirmSettings } from '../../hooks/useDemandLetters';
import BundleReviewModal from '../BundleReviewModal';
import ExtractionProgress from '../ExtractionProgress';
import IntakeProposalCard from '../IntakeProposalCard';
import { PartyBadge } from '../AccountCard';
import type { Statement, JobResponse } from '../../types';
import { jobStatusLabel, jobStatusVariant } from '../../lib/enums';
import api, { extractAxiosDetail } from '../../lib/api';

interface PartyMismatchWarning {
  type: 'party_mismatch_warning';
  holder_name: string;
  client_name: string;
  opposing_name: string;
  client_similarity: number;
  opposing_similarity: number;
  message: string;
}

function getPartyMismatchWarning(parsingWarnings: string | null): PartyMismatchWarning | null {
  if (!parsingWarnings) return null;
  let warnings: unknown[];
  try { warnings = JSON.parse(parsingWarnings); } catch { return null; }
  if (!Array.isArray(warnings)) return null;
  const found = warnings.find(
    (w): w is PartyMismatchWarning =>
      typeof w === 'object' && w !== null && (w as PartyMismatchWarning).type === 'party_mismatch_warning'
  );
  return found ?? null;
}

const MAX_CONCURRENT_UPLOADS = 3;

type LocalFileStatus = 'queued' | 'uploading' | 'review' | 'submitted' | 'error' | 'skipped';

interface FileEntry {
  id: string;
  file: File;
  localStatus: LocalFileStatus;
  jobId: number | null;
  errorMessage?: string;
  /** Set while a scanned production's OCR boundary pass runs — when its job
   *  completes we fetch the segmented bundle and open the review modal. */
  scanBundleId?: number | null;
  /** User-entered Bates range for THIS file (single-file queue only — two
   *  productions can never share a range). */
  bates?: BatesRangeInput | null;
  /** Production-tracker round this file belongs to (whole-batch selection —
   *  one production round can span many files). */
  productionRoundId?: number | null;
}

let fileEntrySeq = 0;
function makeEntryId(): string {
  fileEntrySeq += 1;
  return `f${Date.now().toString(36)}-${fileEntrySeq}`;
}

type TrackerStage =
  | 'queued' | 'uploading' | 'review' | 'processing' | 'done' | 'needs_review' | 'failed' | 'skipped';

interface TrackerItem {
  key: string;
  filename: string;
  stage: TrackerStage;
  progress: number;
  jobId: number | null;
  job?: JobResponse;
  entryId?: string;
  errorMessage?: string;
}

const TRACKER_VISIBLE_LIMIT = 60;
const HISTORY_LIMIT = 30;

interface StagedFile {
  id: string;
  file: File;
  checked: boolean;
  fileType: string;
  sizeStr: string;
  isUploadable: boolean;
}

interface UploadTabProps {
  caseId: string;
  casePublicId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
}


function getJobFilename(job: JobResponse): string {
  if (job.filename) return job.filename;
  if (job.result_data?.filename) return job.result_data.filename;
  return `Job #${job.id}`;
}

// Fallback when an older bundle child has no stored original name: strip the
// "_part_NN" suffix our splitter appends, e.g. "Discovery_part_03.pdf" → "Discovery.pdf".
function stripPartSuffix(name: string): string {
  return name.replace(/_part_\d+(\.pdf)?$/i, '.pdf');
}

// One Upload-history row = one file the user actually uploaded. A row is either
// a standalone job or a collapsed group of bundle-split parts.
interface HistoryRow {
  key: string;
  filename: string;
  status: JobResponse['status'];
  created_at: string | null;
  doneCount: number;
  partTotal: number;
  isBundle: boolean;
}

function getFileTypeLabel(file: File): string {
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf')) return 'PDF';
  if (n.endsWith('.png')) return 'PNG';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'JPG';
  if (n.endsWith('.xlsx') || n.endsWith('.xls')) return 'Excel';
  if (n.endsWith('.docx') || n.endsWith('.doc')) return 'Word';
  if (n.endsWith('.txt')) return 'Text';
  if (n.endsWith('.csv')) return 'CSV';
  if (n.endsWith('.zip') || n.endsWith('.rar') || n.endsWith('.7z')) return 'Archive';
  if (file.type.startsWith('image/')) return 'Image';
  return 'File';
}

const MAX_PDF_BYTES = 200 * 1024 * 1024;
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp'];

function isImageFile(file: File): boolean {
  const n = file.name.toLowerCase();
  return file.type.startsWith('image/') || IMAGE_EXTS.some(ext => n.endsWith(ext));
}

function isFileUploadable(file: File): boolean {
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf')) return file.size <= MAX_PDF_BYTES;
  if (isImageFile(file)) return file.size <= MAX_IMAGE_BYTES;
  return false;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function typeBadgeTone(fileType: string): TagTone {
  if (fileType === 'PDF') return 'good';
  if (fileType === 'PNG' || fileType === 'JPG' || fileType === 'Image') return 'info';
  return 'neutral';
}

async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = [];
  let batch: FileSystemEntry[];
  do {
    batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
    all.push(...batch);
  } while (batch.length > 0);
  return all;
}

async function traverseEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise<File[]>((res, rej) => {
      (entry as FileSystemFileEntry).file(f => res([f]), rej);
    });
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const entries = await readAllEntries(reader);
    const arrays = await Promise.all(entries.map(traverseEntry));
    return arrays.flat();
  }
  return [];
}

export default function UploadTab({ caseId, casePublicId, clientName, opposingPartyName }: UploadTabProps) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploadSourceParty, setUploadSourceParty] = useState<SourceParty | null>(null);
  // Bates range for a single pre-stamped production (opposing/third-party
  // only; hidden for multi-file batches — ranges are per file). Stamps are
  // also auto-detected server-side; typing one here is the manual override.
  const [batesStart, setBatesStart] = useState('');
  const [batesEnd, setBatesEnd] = useState('');
  // Production-tracker round for this batch ('' = no round). Rounds belong to
  // a producing party, so switching the party resets the selection.
  const [uploadRoundId, setUploadRoundId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Statement | null>(null);
  const [totalAdded, setTotalAdded] = useState(0);
  const [showAllTracker, setShowAllTracker] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(new Set());
  // Job ids observed active during this browser session. Lets standalone jobs
  // (bundle children, auto-accepted extras) linger in the tracker as "Done"
  // after they finish, instead of vanishing. Empty after refresh by design.
  const [sessionSeenJobIds, setSessionSeenJobIds] = useState<Set<number>>(new Set());
  // Bundle review payloads keyed by the originating file entry id, so a row can
  // re-open its review modal and stay visible while awaiting the user's decision.
  const [reviewBundles, setReviewBundles] = useState<Record<string, {
    bundleId: number; filename: string; pageCount: number;
    boundaries: DetectedBoundary[]; originalFileName: string;
  }>>({});
  const [pendingBundleEntryId, setPendingBundleEntryId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadingCountRef = useRef(0);
  const startedUploadsRef = useRef<Set<string>>(new Set());
  const lastDedupCountRef = useRef(0);
  const didInitCollapse = useRef(false);
  const queryClient = useQueryClient();

  const { data: statements = [], isLoading: statementsLoading } = useStatements(caseId);
  const deleteStatement = useDeleteStatement();
  const { data: recentJobs = [] } = useCaseJobs(caseId);
  const retryJob = useRetryJob();
  const deleteJob = useDeleteJob();
  const deleteAllFailedJobs = useDeleteAllFailedJobs();
  const forceResetJob = useForceResetJob();
  const [forceResetTarget, setForceResetTarget] = useState<JobResponse | null>(null);

  // ── Smart Intake proposals (non-statement documents awaiting review) ──
  const { data: intakeProposals = [] } = useIntakeProposals(caseId);
  const { data: caseData } = useCase(casePublicId);
  const { data: firmSettings } = useFirmSettings();
  const activeEngine = firmSettings?.statement_engine === 'llamaparse' ? 'llamaparse' : 'claude';
  const currentCaseFields = useMemo<Record<string, unknown>>(() => {
    if (!caseData) return {};
    return {
      cause_number: caseData.cause_number,
      court: caseData.court,
      county: caseData.county,
      client_name: caseData.client_name,
      opposing_party_name: caseData.opposing_party_name,
      client_role: caseData.client_role,
      marriage_date: caseData.marriage_date,
      separation_date: caseData.separation_date,
      opposing_counsel_name: caseData.opposing_counsel_name,
      opposing_counsel_firm: caseData.opposing_counsel_firm,
      opposing_counsel_address: caseData.opposing_counsel_address,
      opposing_counsel_email: caseData.opposing_counsel_email,
      filing_date: caseData.filing_date,
      trial_date: caseData.trial_date,
      mediation_date: caseData.mediation_date,
      temp_orders_date: caseData.temp_orders_date,
      divorce_decree_date: caseData.divorce_decree_date,
    };
  }, [caseData]);
  // Lightweight read of the Production Tracker aggregate — only to offer the
  // "Production round" choices for this batch. Tolerant local shape: the full
  // payload is owned by useProductionTracker.ts.
  interface UploadTrackerRound { id: number; round_number: number; label?: string | null }
  interface UploadTrackerParty { producing_party?: string | null; rounds?: UploadTrackerRound[] }
  const { data: trackerData } = useQuery<{ parties?: UploadTrackerParty[] }>({
    queryKey: ['production-tracker', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/production-tracker`);
      return data;
    },
    enabled: !!caseId,
  });
  const roundOptions = useMemo(() => {
    if (uploadSourceParty !== 'opposing' && uploadSourceParty !== 'third_party') return [];
    const party = (trackerData?.parties ?? []).find(p => p.producing_party === uploadSourceParty);
    return (party?.rounds ?? []).map(r => ({
      value: String(r.id),
      label: r.label || `RFP ${r.round_number}`,
    }));
  }, [trackerData, uploadSourceParty]);

  const confirmProposal = useConfirmProposal(caseId);
  const dismissProposal = useDismissProposal(caseId);
  const reclassifyProposal = useReclassifyProposal(caseId);
  const intakeBusy =
    confirmProposal.isPending || dismissProposal.isPending || reclassifyProposal.isPending;

  // (JobProgressDetail is defined below the main component)

  // ── Bundle (multi-statement PDF) state ─────────────────────────────────────
  const [pendingBundle, setPendingBundle] = useState<{
    bundleId: number;
    filename: string;
    pageCount: number;
    boundaries: DetectedBoundary[];
    originalFileName: string;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  // A confirm that 422s/404s must say so — a cleared spinner with a card that
  // just sits there pending reads as "the button does nothing".
  const [intakeError, setIntakeError] = useState<string | null>(null);

  // A pending job that has used up all its retries will never be picked up
  // by the worker again — treat it as failed in the UI so the user can see
  // and delete it.
  const isStuckPending = (j: JobResponse) =>
    j.status === 'pending' &&
    !j.locked_by &&
    (j.attempts ?? 0) >= (j.max_attempts ?? 3);
  const failedJobs = recentJobs.filter(j => j.status === 'failed' || isStuckPending(j));

  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  useEffect(() => {
    const hasTerminal = recentJobs.some(j => j.status === 'completed' || j.status === 'needs_review');
    if (hasTerminal) {
      queryClient.invalidateQueries({ queryKey: ['statements', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
      queryClient.invalidateQueries({ queryKey: ['discovery-leads', caseId] });
      queryClient.invalidateQueries({ queryKey: ['missing-gaps', caseId] });
    }
  }, [recentJobs, caseId, queryClient]);

  useEffect(() => {
    if (files.length === 0 && totalAdded > 0) setTotalAdded(0);
  }, [files.length, totalAdded]);

  // Remember every job we see active this session, so completed standalone jobs
  // (bundle children, auto-accepted extras) linger in the tracker instead of
  // vanishing the moment they finish.
  useEffect(() => {
    setSessionSeenJobIds(prev => {
      let next = prev;
      let changed = false;
      const ensure = () => { if (!changed) { next = new Set(prev); changed = true; } };
      for (const j of recentJobs) {
        if ((j.status === 'pending' || j.status === 'processing') && !next.has(j.id)) {
          ensure();
          next.add(j.id);
        }
      }
      // Bounded prune: once the set grows large, drop ids the poll no longer
      // returns (aged-out completed jobs) so it can't grow unbounded in a long
      // session. Only triggers past the cap, so freshly-remembered ids are safe.
      const SEEN_CAP = 300;
      const size = changed ? next.size : prev.size;
      if (size > SEEN_CAP) {
        const present = new Set(recentJobs.map(j => j.id));
        const pruned = new Set([...(changed ? next : prev)].filter(id => present.has(id)));
        if (pruned.size !== size) { next = pruned; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [recentJobs]);

  const rememberJobs = useCallback((ids: Array<number | null | undefined>) => {
    const valid = ids.filter((x): x is number => typeof x === 'number');
    if (valid.length === 0) return;
    setSessionSeenJobIds(prev => {
      const next = new Set(prev);
      for (const id of valid) next.add(id);
      return next;
    });
  }, []);

  const startUpload = useCallback(async (entry: FileEntry) => {
    const id = entry.id;
    const file = entry.file;
    if (startedUploadsRef.current.has(id)) return;
    if (uploadingCountRef.current >= MAX_CONCURRENT_UPLOADS) return;

    startedUploadsRef.current.add(id);
    uploadingCountRef.current++;

    const setEntry = (patch: Partial<FileEntry>) =>
      setFiles(p => p.map(f => (f.id === id ? { ...f, ...patch } : f)));

    setEntry({ localStatus: 'uploading' });

    try {
      // Pre-scan first: a single-statement PDF falls back to the normal upload,
      // a multi-statement bundle either auto-accepts (silent) or opens the review modal.
      const detected = await detectBundle(caseId, file, uploadSourceParty, entry.bates, entry.productionRoundId);

      if (detected.status === 'skipped') {
        setEntry({ localStatus: 'skipped', errorMessage: detected.message ?? 'This exact file has already been uploaded for this case' });
      } else if (detected.status === 'single_statement') {
        // Fall through to the normal single-statement upload path
        const result = await uploadFile(caseId, file, uploadSourceParty, entry.bates, entry.productionRoundId);
        if (result.status === 'skipped') {
          setEntry({ localStatus: 'skipped', errorMessage: result.message });
        } else {
          // Keep the row visible — it now tracks the backend job in place.
          setEntry({ localStatus: 'submitted', jobId: result.job_id });
          rememberJobs([result.job_id]);
          queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
        }
      } else if (detected.status === 'queued') {
        // Single statement queued directly by the pre-scan — the file reached the
        // server only ONCE (no second /upload round-trip). Track the job in place.
        setEntry({ localStatus: 'submitted', jobId: detected.job_id ?? null });
        rememberJobs([detected.job_id]);
        queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
      } else if (detected.status === 'auto_accepted') {
        // All bank formats already trusted — queued silently
        setEntry({ localStatus: 'submitted', jobId: detected.job_ids?.[0] ?? null });
        rememberJobs(detected.job_ids ?? []);
        queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
        const n = detected.statements_created ?? detected.job_ids?.length ?? 0;
        setToast(`Queued ${n} statement${n !== 1 ? 's' : ''} from "${file.name}" (bank formats already confirmed).`);
      } else if (detected.status === 'intake_queued') {
        // The cheap header scan didn't conclusively recognize a statement, so the
        // file is being read to confirm what it is. If it's a statement (or simply
        // unrecognized), it routes straight to the statement pipeline; only a
        // clearly different document type (pay stub, tax return, valuation, legal
        // filing) becomes a proposal below.
        setEntry({ localStatus: 'submitted', jobId: detected.job_id ?? null });
        rememberJobs([detected.job_id]);
        queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
        queryClient.invalidateQueries({ queryKey: ['intakeProposals', caseId] });
        setToast(`Reading "${file.name}" to confirm what it is...`);
      } else if (detected.status === 'scanning') {
        // Scanned production: the OCR boundary pass runs as a job. Track it in
        // place; the review modal opens automatically when it finishes.
        setEntry({ localStatus: 'submitted', jobId: detected.job_id ?? null, scanBundleId: detected.bundle_id ?? null });
        rememberJobs([detected.job_id]);
        queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
        setToast(detected.message ?? `"${file.name}" is scanned — reading every page to find what's inside (a few minutes).`);
      } else if (detected.status === 'review_required') {
        // Multi-statement bundle: keep the row visible as "Needs your review"
        // and open the review modal. The row stays until the user decides.
        const payload = {
          bundleId: detected.bundle_id!,
          filename: detected.filename ?? file.name,
          pageCount: detected.page_count ?? 0,
          boundaries: detected.boundaries ?? [],
          originalFileName: file.name,
        };
        setReviewBundles(prev => ({ ...prev, [id]: payload }));
        setPendingBundle(payload);
        setPendingBundleEntryId(id);
        setEntry({ localStatus: 'review' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setEntry({ localStatus: 'error', errorMessage: msg });
    } finally {
      startedUploadsRef.current.delete(id);
      uploadingCountRef.current--;
    }
  }, [caseId, queryClient, uploadSourceParty, rememberJobs]);

  useEffect(() => {
    const queuedFiles = files.filter(f => f.localStatus === 'queued');
    if (queuedFiles.length === 0) return;
    const slotsAvailable = MAX_CONCURRENT_UPLOADS - uploadingCountRef.current;
    if (slotsAvailable <= 0) return;
    queuedFiles
      .filter(f => !startedUploadsRef.current.has(f.id))
      .slice(0, slotsAvailable)
      .forEach(entry => startUpload(entry));
  }, [files, startUpload]);

  // Scanned productions: when an entry's OCR boundary-pass job completes,
  // fetch the segmented bundle and open the review modal automatically.
  const handledScanBundlesRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    for (const entry of files) {
      if (entry.scanBundleId == null || entry.jobId == null) continue;
      if (handledScanBundlesRef.current.has(entry.scanBundleId)) continue;
      const job = recentJobs.find(j => j.id === entry.jobId);
      if (!job) continue;
      if (job.status === 'completed') {
        const bundleId = entry.scanBundleId;
        handledScanBundlesRef.current.add(bundleId);
        (async () => {
          try {
            const b = await getBundle(bundleId);
            const payload = {
              bundleId,
              filename: b.filename ?? entry.file.name,
              pageCount: b.page_count ?? 0,
              boundaries: b.boundaries ?? [],
              originalFileName: entry.file.name,
            };
            setReviewBundles(prev => ({ ...prev, [entry.id]: payload }));
            setPendingBundle(payload);
            setPendingBundleEntryId(entry.id);
            setFiles(p => p.map(f => f.id === entry.id ? { ...f, localStatus: 'review', scanBundleId: null } : f));
          } catch {
            // Leave the row tracking the job; the user can retry from there.
            handledScanBundlesRef.current.delete(bundleId);
          }
        })();
      } else if (job.status === 'failed') {
        handledScanBundlesRef.current.add(entry.scanBundleId);
        setFiles(p => p.map(f => f.id === entry.id
          ? { ...f, localStatus: 'error', errorMessage: job.error_message ?? 'Scan failed', scanBundleId: null }
          : f));
      }
    }
  }, [files, recentJobs]);

  const addFiles = useCallback((newFiles: File[], bates?: BatesRangeInput | null, productionRoundId?: number | null) => {
    const uploadable = newFiles.filter((f) => isFileUploadable(f));
    if (uploadable.length === 0) return;
    lastDedupCountRef.current = 0;
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.file.name.toLowerCase()));
      const dedupedFiles = uploadable.filter((f) => !existingNames.has(f.name.toLowerCase()));
      if (dedupedFiles.length === 0) return prev;
      lastDedupCountRef.current = dedupedFiles.length;
      return [
        ...prev,
        ...dedupedFiles.map((file) => ({
          id: makeEntryId(), file, localStatus: 'queued' as LocalFileStatus, jobId: null,
          // A typed range only ever describes ONE file.
          bates: dedupedFiles.length === 1 ? bates ?? null : null,
          // A round describes the whole batch — every file it contains.
          productionRoundId: productionRoundId ?? null,
        })),
      ];
    });
    setTimeout(() => {
      if (lastDedupCountRef.current > 0) {
        setTotalAdded(prev => prev + lastDedupCountRef.current);
        lastDedupCountRef.current = 0;
      }
    }, 0);
  }, []);

  const addToStaging = useCallback((newFiles: File[]) => {
    if (newFiles.length === 0) return;
    setStagedFiles(prev => {
      const existingIds = new Set(prev.map(f => f.id));
      const alreadyQueued = new Set(files.map(f => f.file.name.toLowerCase()));
      const toAdd: StagedFile[] = [];
      for (const file of newFiles) {
        const id = `${file.name}-${file.size}-${file.lastModified}`;
        if (existingIds.has(id)) continue;
        if (alreadyQueued.has(file.name.toLowerCase())) continue;
        const uploadable = isFileUploadable(file);
        toAdd.push({
          id,
          file,
          checked: uploadable,
          fileType: getFileTypeLabel(file),
          sizeStr: formatFileSize(file.size),
          isUploadable: uploadable,
        });
      }
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, [files]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const items = Array.from(e.dataTransfer.items);
    const allFiles: File[] = [];
    for (const item of items) {
      const entry = item.webkitGetAsEntry?.();
      if (entry) {
        const found = await traverseEntry(entry);
        allFiles.push(...found);
      } else if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) allFiles.push(f);
      }
    }
    addToStaging(allFiles);
  }, [addToStaging]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addToStaging(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [addToStaging]);

  const handleFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addToStaging(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [addToStaging]);

  const toggleStagedFile = useCallback((id: string, checked: boolean) => {
    setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, checked } : f));
  }, []);

  const toggleAllStaged = useCallback((checked: boolean) => {
    setStagedFiles(prev => prev.map(f => f.isUploadable ? { ...f, checked } : f));
  }, []);

  const removeStagedFile = useCallback((id: string) => {
    setStagedFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const queueCheckedFiles = useCallback(() => {
    const toQueue = stagedFiles.filter(f => f.checked && f.isUploadable).map(f => f.file);
    setStagedFiles(prev => prev.filter(f => !(f.checked && f.isUploadable)));
    const bates =
      toQueue.length === 1 && batesStart.trim()
        ? { start: batesStart, end: batesEnd || undefined }
        : null;
    const roundId = uploadRoundId ? Number(uploadRoundId) : null;
    if (toQueue.length > 0) addFiles(toQueue, bates, roundId);
    setBatesStart('');
    setBatesEnd('');
  }, [stagedFiles, addFiles, batesStart, batesEnd, uploadRoundId]);

  const removeEntry = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setReviewBundles(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setTotalAdded(prev => Math.max(0, prev - 1));
  }, []);

  const handleDeleteStatement = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteStatement.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, deleteStatement]);

  const handleRetry = useCallback(async (jobId: number) => {
    await retryJob.mutateAsync(jobId);
    queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
  }, [retryJob, caseId, queryClient]);

  const handleDeleteAllFailed = useCallback(async () => {
    setDeletingAll(true);
    try {
      await deleteAllFailedJobs.mutateAsync(caseId);
    } finally {
      setDeletingAll(false);
    }
  }, [deleteAllFailedJobs, caseId]);

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ── Derived staging stats ─────────────────────────────────────────────────
  const stagedPdfs = stagedFiles.filter(f => f.isUploadable);
  const stagedOther = stagedFiles.filter(f => !f.isUploadable);
  const checkedCount = stagedFiles.filter(f => f.checked && f.isUploadable).length;
  const allPdfsChecked = stagedPdfs.length > 0 && stagedPdfs.every(f => f.checked);

  // ── Unified upload tracker ────────────────────────────────────────────────
  // ONE list that follows every file from drop → done. A file entry keeps its
  // stable client id (key `local:<id>`) the entire time, attaching its backend
  // job once uploaded so the row advances in place — it never disappears between
  // stages. Standalone backend jobs we didn't originate locally (e.g. bundle
  // children, or jobs from a previous session still finishing) appear keyed
  // `job:<id>` so nothing in flight is ever hidden.
  const recentJobsById = useMemo(() => {
    const m = new Map<number, JobResponse>();
    for (const j of recentJobs) m.set(j.id, j);
    return m;
  }, [recentJobs]);

  const jobStage = useCallback((job: JobResponse): TrackerStage => {
    switch (job.status) {
      case 'processing': return 'processing';
      case 'completed': return 'done';
      case 'needs_review': return 'needs_review';
      case 'failed': return 'failed';
      case 'pending': return isStuckPending(job) ? 'failed' : 'queued';
      default: return 'queued';
    }
  }, []);

  const trackerItems = useMemo<TrackerItem[]>(() => {
    const items: TrackerItem[] = [];
    const claimedJobIds = new Set<number>();

    // 1) Local file entries — these own their row from the moment of drop.
    for (const entry of files) {
      const job = entry.jobId != null ? recentJobsById.get(entry.jobId) : undefined;
      if (job) claimedJobIds.add(job.id);

      let stage: TrackerStage;
      let progress = 0;
      if (entry.localStatus === 'queued') { stage = 'queued'; progress = 0; }
      else if (entry.localStatus === 'uploading') { stage = 'uploading'; progress = 8; }
      else if (entry.localStatus === 'review') { stage = 'review'; progress = 0; }
      else if (entry.localStatus === 'skipped') { stage = 'skipped'; progress = 100; }
      else if (entry.localStatus === 'error') { stage = 'failed'; progress = 0; }
      else if (job) { stage = jobStage(job); progress = job.progress ?? 0; }
      else { stage = 'queued'; progress = 12; } // submitted, job not yet visible

      if (stage === 'done') progress = 100;
      items.push({
        key: `local:${entry.id}`,
        filename: entry.file.name,
        stage,
        progress,
        jobId: entry.jobId,
        job,
        entryId: entry.id,
        errorMessage: entry.errorMessage ?? job?.error_message ?? undefined,
      });
    }

    // 2) Standalone jobs not tied to a local entry. Show while active, or once
    //    completed only if we saw them active this session (so bundle children
    //    linger as "Done" instead of vanishing). Skip historical completed jobs.
    for (const job of recentJobs) {
      if (claimedJobIds.has(job.id)) continue;
      const active = job.status === 'pending' || job.status === 'processing';
      const finished = job.status === 'completed' || job.status === 'needs_review';
      const failed = job.status === 'failed' || isStuckPending(job);
      const seen = sessionSeenJobIds.has(job.id);
      if (!active && !failed && !(finished && seen)) continue;
      const stage = jobStage(job);
      items.push({
        key: `job:${job.id}`,
        filename: getJobFilename(job),
        stage,
        progress: stage === 'done' ? 100 : (job.progress ?? 0),
        jobId: job.id,
        job,
        errorMessage: job.error_message ?? undefined,
      });
    }

    // Keep insertion order (dropped files first, in drop order, then standalone
    // jobs) so a row never jumps position as its status advances — it changes in
    // place. This is the calmest, most continuous behavior, which is the goal.
    return items;
  }, [files, recentJobs, recentJobsById, sessionSeenJobIds, jobStage]);

  const trackerCounts = useMemo(() => {
    const c = { waiting: 0, uploading: 0, processing: 0, done: 0, attention: 0 };
    for (const it of trackerItems) {
      if (it.stage === 'queued') c.waiting++;
      else if (it.stage === 'uploading') c.uploading++;
      else if (it.stage === 'processing') c.processing++;
      else if (it.stage === 'done') c.done++;
      else c.attention++; // failed, needs_review, review, skipped
    }
    return c;
  }, [trackerItems]);

  const trackerTotal = trackerItems.length;

  // One aggregate progress number for the whole batch. We weight by each
  // statement's own progress (a finished/failed/imported statement counts as
  // 100 so a single failure never strands the bar short of done). Items still
  // awaiting the user's review are excluded — they aren't "processing".
  const overallPercent = useMemo(() => {
    const barItems = trackerItems.filter(it => it.stage !== 'review');
    if (barItems.length === 0) return 0;
    const sum = barItems.reduce((acc, it) => {
      if (it.stage === 'done' || it.stage === 'needs_review' || it.stage === 'failed' || it.stage === 'skipped') {
        return acc + 100;
      }
      return acc + (it.progress || 0);
    }, 0);
    return Math.min(100, Math.round(sum / barItems.length));
  }, [trackerItems]);

  const isExtracting =
    trackerCounts.waiting + trackerCounts.uploading + trackerCounts.processing > 0;

  const hasFinishedTrackerItems = trackerItems.some(
    it => it.stage === 'done' || it.stage === 'failed' || it.stage === 'skipped'
  );

  // By default the list only surfaces items that need the user to act
  // (bundle review, failures, imported-but-flagged). "Show details" reveals
  // every row so power users can watch each statement.
  const attentionTrackerItems = useMemo(
    () => trackerItems.filter(
      it => it.stage === 'review' || it.stage === 'failed' || it.stage === 'needs_review' || it.stage === 'skipped'
    ),
    [trackerItems]
  );
  const listSource = showDetails ? trackerItems : attentionTrackerItems;
  const visibleTrackerItems = showAllTracker
    ? listSource
    : listSource.slice(0, TRACKER_VISIBLE_LIMIT);

  const clearFinished = useCallback(() => {
    // Drop finished/failed local rows and stop lingering finished standalone jobs.
    setFiles(prev => {
      const kept = prev.filter(f => {
        const job = f.jobId != null ? recentJobsById.get(f.jobId) : undefined;
        const localDone = f.localStatus === 'error' || f.localStatus === 'skipped';
        const jobDone = job ? (job.status === 'completed' || job.status === 'needs_review'
          || job.status === 'failed' || isStuckPending(job)) : false;
        return !(localDone || jobDone);
      });
      const removed = prev.length - kept.length;
      if (removed > 0) setTotalAdded(t => Math.max(0, t - removed));
      return kept;
    });
    setSessionSeenJobIds(prev => {
      const next = new Set<number>();
      for (const id of prev) {
        const job = recentJobsById.get(id);
        const isFinished = job && (job.status === 'completed' || job.status === 'needs_review'
          || job.status === 'failed' || isStuckPending(job));
        if (!isFinished) next.add(id);
      }
      return next;
    });
  }, [recentJobsById]);

  // ── Upload history (modest, collapsed-by-default chronological panel) ──────
  // History is keyed by what the USER actually uploaded. When a single PDF is
  // split into N child statements, all parts collapse into ONE row showing the
  // original filename and a rolled-up status — not N separate part rows.
  const uploadHistory = useMemo<HistoryRow[]>(() => {
    const bundles = new Map<number, JobResponse[]>();
    const singles: JobResponse[] = [];

    for (const job of recentJobs) {
      if (job.bundle_id != null) {
        const arr = bundles.get(job.bundle_id) ?? [];
        arr.push(job);
        bundles.set(job.bundle_id, arr);
      } else {
        singles.push(job);
      }
    }

    const rows: HistoryRow[] = [];

    for (const job of singles) {
      rows.push({
        key: `job-${job.id}`,
        filename: getJobFilename(job),
        status: job.status,
        created_at: job.created_at,
        doneCount: job.status === 'completed' || job.status === 'needs_review' ? 1 : 0,
        partTotal: 1,
        isBundle: false,
      });
    }

    for (const [bundleId, parts] of bundles) {
      const statuses = parts.map(p => p.status);
      const hasActive = statuses.some(s => s === 'pending' || s === 'processing');
      const failedCount = statuses.filter(s => s === 'failed').length;
      const needsReviewCount = statuses.filter(s => s === 'needs_review').length;
      const doneCount = statuses.filter(s => s === 'completed' || s === 'needs_review').length;
      const status: JobResponse['status'] = hasActive
        ? 'processing'
        : failedCount > 0
          ? 'failed'
          : needsReviewCount > 0
            ? 'needs_review'
            : 'completed';
      // bundle_total is authoritative (some parts may be outside the last-30
      // window); fall back to how many parts we actually see.
      const partTotal = parts.find(p => p.bundle_total != null)?.bundle_total ?? parts.length;
      // Sort by upload time so the group sits where the user dropped the file.
      const latest = parts.reduce((max, p) => {
        const t = p.created_at ? Date.parse(p.created_at) : 0;
        return t > max ? t : max;
      }, 0);
      const original =
        parts.find(p => p.bundle_filename)?.bundle_filename
        ?? stripPartSuffix(getJobFilename(parts[0]));
      rows.push({
        key: `bundle-${bundleId}`,
        filename: original,
        status,
        created_at: latest ? new Date(latest).toISOString() : parts[0]?.created_at ?? null,
        doneCount,
        partTotal,
        isBundle: true,
      });
    }

    return rows
      .sort((a, b) => {
        const ta = a.created_at ? Date.parse(a.created_at) : 0;
        const tb = b.created_at ? Date.parse(b.created_at) : 0;
        return tb - ta;
      })
      .slice(0, HISTORY_LIMIT);
  }, [recentJobs]);

  const groupedStatements = statements.reduce<Record<string, Statement[]>>((acc, stmt) => {
    const key = stmt.bank_label || 'Unknown Account';
    if (!acc[key]) acc[key] = [];
    acc[key].push(stmt);
    return acc;
  }, {});

  // Sort each account's statements by start date ascending
  Object.values(groupedStatements).forEach(stmts => {
    stmts.sort((a, b) => {
      if (!a.statement_start_date) return 1;
      if (!b.statement_start_date) return -1;
      return a.statement_start_date.localeCompare(b.statement_start_date);
    });
  });

  const accountLabels = Object.keys(groupedStatements);

  const toggleAccount = (label: string) => {
    setCollapsedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Auto-collapse on first load; collapse any newly-appearing account labels thereafter
  useEffect(() => {
    if (statements.length === 0) return;
    const labels = [...new Set(statements.map(s => s.bank_label || 'Unknown Account'))];
    if (!didInitCollapse.current) {
      didInitCollapse.current = true;
      if (statements.length > 1) {
        setCollapsedAccounts(new Set(labels));
      }
    } else {
      // Collapse any brand-new account labels that weren't present before
      setCollapsedAccounts(prev => {
        const next = new Set(prev);
        let changed = false;
        for (const label of labels) {
          if (!next.has(label) && statements.filter(s => (s.bank_label || 'Unknown Account') === label).length === 1) {
            next.add(label);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [statements]);

  const formatMonthYear = (d: string | null) => {
    if (!d) return '—';
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Render a date range cleanly: drop the dash when one side is missing or both
  // sides format to the same value, so single statements don't show "X – —".
  const formatRange = (
    start: string | null,
    end: string | null,
    fmt: (d: string | null) => string,
  ) => {
    if (start && end) {
      const a = fmt(start);
      const b = fmt(end);
      return a === b ? a : `${a} – ${b}`;
    }
    if (start) return fmt(start);
    if (end) return fmt(end);
    return '—';
  };

  const formatUploadDate = (d: string) => {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h2 className="t-eyebrow">Upload Statements</h2>
        <Link
          to="/firm-settings"
          className="inline-flex h-7 items-center gap-2 rounded-md border border-border bg-cream-light px-3 text-xs font-medium text-ink-soft hover:border-green-300 hover:text-green-800 transition-colors"
          title="Change the statement reading engine in Firm Settings"
        >
          {activeEngine === 'llamaparse' ? (
            <ScanLine size={12} className="text-green-800" />
          ) : (
            <FileText size={12} className="text-green-800" />
          )}
          <span>Reader: {activeEngine === 'llamaparse' ? 'High-accuracy OCR' : 'Standard'}</span>
        </Link>
      </div>

      {/* ── Toast (auto-clears after 5s) ── */}
      {toast && (
        <div className="rounded-lg border border-green-200 bg-green-100 px-4 py-3 text-sm text-green-900 flex items-center justify-between gap-3">
          <span>{toast}</span>
          <IconButton
            icon={XCircle}
            size="xs"
            variant="ghost"
            aria-label="Dismiss"
            onClick={() => setToast(null)}
            className="text-green-700 hover:bg-transparent hover:text-green-900"
          />
        </div>
      )}

      {/* ── Smart Intake: documents that aren't bank/credit statements ── */}
      {intakeProposals.length > 0 && (
        <div className="rounded-lg border border-clay-200 bg-cream p-4">
          <div className="mb-3 flex items-center gap-2">
            <ListChecks size={16} className="text-clay-700" />
            <h3 className="text-sm font-semibold text-green-900">
              Documents to review
              <span className="ml-2 font-normal text-ink-faint">
                ({intakeProposals.length} pending)
              </span>
            </h3>
          </div>
          <p className="mb-3 text-sm text-ink-soft">
            These files don't look like bank or credit card statements. Confirm what to do with each one.
          </p>
          {intakeError && (
            <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-lg border border-danger bg-danger-soft text-sm text-danger">
              <span className="flex-1">{intakeError}</span>
              <IconButton
                icon={XCircle}
                size="xs"
                variant="ghost"
                aria-label="Dismiss"
                onClick={() => setIntakeError(null)}
                className="shrink-0 text-danger hover:bg-transparent hover:text-danger"
              />
            </div>
          )}
          <div className="space-y-3">
            {intakeProposals.map((p) => (
              <IntakeProposalCard
                key={p.id}
                proposal={p}
                caseFields={currentCaseFields}
                busy={intakeBusy}
                onConfirm={(id, applyFields, spouse, incomeOverrides) => {
                  setIntakeError(null);
                  confirmProposal.mutate(
                    { proposalId: id, applyFields, spouse, incomeOverrides },
                    {
                      onError: (e) =>
                        setIntakeError(extractAxiosDetail(e) || 'Could not confirm this document.'),
                    },
                  );
                }}
                onDismiss={(id) => dismissProposal.mutate(id)}
                onReclassify={(id, docType) => reclassifyProposal.mutate({ proposalId: id, doc_type: docType })}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Drop zone ── */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver
            ? 'border-green-500 bg-green-100'
            : 'border-border-strong hover:border-green-300 hover:bg-green-100/30'
        }`}
      >
        <div className={`mx-auto mb-3 w-14 h-14 rounded-lg flex items-center justify-center transition-colors ${
          dragOver ? 'bg-green-200' : 'bg-green-100'
        }`}>
          <Upload size={24} className="text-green-700" />
        </div>
        <p className="t-card text-green-900 mb-1">
          {dragOver ? 'Release to stage files' : 'Drop files or folders here'}
        </p>
        <p className="text-sm text-ink-faint mb-4">
          Any file type accepted · PDFs will be auto-selected for processing
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button size="lg" icon={FileText} onClick={() => fileInputRef.current?.click()}>
            Select Files
          </Button>
          <Button variant="secondary" size="lg" icon={FolderOpen} onClick={() => folderInputRef.current?.click()}>
            Select Folder
          </Button>
        </div>
        {/* eslint-disable-next-line no-restricted-syntax -- hidden native file input: no ui primitive for programmatic pickers */}
        <input
          ref={fileInputRef}
          type="file"
          accept="*/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        {/* eslint-disable-next-line no-restricted-syntax -- hidden native file input: no ui primitive for programmatic pickers */}
        <input
          ref={folderInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFolderSelect}
        />
      </div>

      {/* ── Staging Panel ── */}
      {stagedFiles.length > 0 && (
        <div className="rounded-lg border border-clay-200 bg-clay-50/60 overflow-hidden">

          {/* Header */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-clay-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-clay-100 flex items-center justify-center flex-shrink-0">
                <ListChecks size={16} className="text-clay-700" />
              </div>
              <div>
                <h3 className="t-card text-clay-900">
                  Review Before Uploading
                  <span className="ml-2 text-sm font-normal text-clay-600">
                    {stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} staged
                  </span>
                </h3>
                <p className="text-xs text-clay-600 mt-1">
                  {stagedPdfs.length} PDF{stagedPdfs.length !== 1 ? 's' : ''}
                  {stagedOther.length > 0 && ` · ${stagedOther.length} other (will be skipped)`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setStagedFiles([])}>
                Clear all
              </Button>
              <Button
                size="lg"
                icon={ArrowUpCircle}
                onClick={queueCheckedFiles}
                disabled={checkedCount === 0 || uploadSourceParty === null}
                title={uploadSourceParty === null ? 'First select who produced these documents' : undefined}
              >
                Queue {checkedCount > 0 ? checkedCount : ''} PDF{checkedCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>

          {/* Provenance for this batch: who handed these documents to the firm.
              Known unambiguously at upload — required before queueing, never inferred. */}
          <div className={`px-5 py-2 border-b flex items-center gap-2 flex-wrap ${
            uploadSourceParty === null ? 'border-warn bg-warn-soft' : 'border-clay-100'
          }`}>
            <span className="text-xs text-clay-800 font-semibold flex-shrink-0">Who produced these documents?</span>
            {([
              ['client', clientName ? `${clientName} (your client)` : 'Your client'],
              ['opposing', opposingPartyName ? `${opposingPartyName} (opposing side)` : 'Opposing side'],
              ['third_party', 'Third party / subpoena'],
            ] as [SourceParty, string][]).map(([value, label]) => (
              <ToolbarChip
                key={String(value)}
                selected={uploadSourceParty === value}
                onClick={() => {
                  setUploadSourceParty(value);
                  // Rounds belong to a producing party — a stale selection
                  // would silently file the batch under the wrong side.
                  if (value !== uploadSourceParty) setUploadRoundId('');
                }}
                className={
                  uploadSourceParty === value
                    ? value === 'client'
                      ? 'border-party-client bg-party-client text-white hover:bg-party-client'
                      : value === 'opposing'
                        ? 'border-party-opposing bg-party-opposing text-white hover:bg-party-opposing'
                        : 'border-party-third bg-party-third text-white hover:bg-party-third'
                    : undefined
                }
              >
                {label}
              </ToolbarChip>
            ))}
            {uploadSourceParty === null && (
              <span className="text-xs text-warn">Select one to enable queueing — this tags the whole batch.</span>
            )}
            {(uploadSourceParty === 'opposing' || uploadSourceParty === 'third_party') &&
              (roundOptions.length > 0 || checkedCount === 1) && (
              <span className="flex items-center gap-2 ml-auto">
                {roundOptions.length > 0 && (
                  /* Production Tracker round this batch belongs to — files it
                     in the round's Bates sequence and per-item accounting. */
                  <>
                    <span className="text-xs text-clay-700 font-medium flex-shrink-0">Production round</span>
                    <Select
                      size="sm"
                      value={uploadRoundId}
                      onChange={(e) => setUploadRoundId(e.target.value)}
                      options={roundOptions}
                      placeholder="No round"
                      aria-label="Production round"
                      className="w-32"
                    />
                  </>
                )}
                {checkedCount === 1 && (
                  /* Pre-stamped production: stamps are read automatically; typing
                     the range here overrides detection. Single file only — two
                     productions can never share a Bates range. */
                  <>
                    <span className="text-xs text-clay-700 font-medium flex-shrink-0">Bates range (optional)</span>
                    <Input
                      size="sm"
                      value={batesStart}
                      onChange={(e) => setBatesStart(e.target.value)}
                      placeholder="SMITH000001"
                      aria-label="Bates range start"
                      className="w-36 t-ident"
                    />
                    <span className="text-xs text-ink-faint">–</span>
                    <Input
                      size="sm"
                      value={batesEnd}
                      onChange={(e) => setBatesEnd(e.target.value)}
                      placeholder="SMITH000483"
                      aria-label="Bates range end"
                      className="w-36 t-ident"
                    />
                  </>
                )}
              </span>
            )}
          </div>

          {/* Quick-select bar */}
          <div className="px-5 py-2 border-b border-clay-100 flex items-center gap-3">
            <span className="text-xs text-clay-700 font-medium">Quick select:</span>
            <Button variant="ghost" size="sm" onClick={() => toggleAllStaged(true)}>
              All PDFs ({stagedPdfs.length})
            </Button>
            <Button variant="ghost" size="sm" onClick={() => toggleAllStaged(false)}>
              Deselect all
            </Button>
            {checkedCount < stagedPdfs.length && (
              <span className="text-xs text-clay-600 ml-auto">
                {checkedCount} of {stagedPdfs.length} PDFs selected
              </span>
            )}
            {checkedCount === stagedPdfs.length && stagedPdfs.length > 0 && (
              <span className="text-xs text-green-700 font-medium ml-auto">
                All {stagedPdfs.length} PDFs selected
              </span>
            )}
          </div>

          {/* Column header */}
          <div className="px-5 py-2 grid grid-cols-[20px_1fr_60px_70px_24px] gap-3 items-center border-b border-clay-100">
            {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
            <input
              type="checkbox"
              checked={allPdfsChecked}
              onChange={e => toggleAllStaged(e.target.checked)}
              className="w-4 h-4 rounded accent-green-600 cursor-pointer"
              title="Select / deselect all PDFs"
            />
            <span className="t-eyebrow">Filename</span>
            <span className="t-eyebrow">Type</span>
            <span className="t-eyebrow text-right">Size</span>
            <span />
          </div>

          {/* File list */}
          <div className="max-h-80 overflow-y-auto divide-y divide-clay-50">
            {stagedFiles.map(sf => (
              <div
                key={sf.id}
                className={`px-5 py-2 grid grid-cols-[20px_1fr_60px_70px_24px] gap-3 items-center transition-colors ${
                  sf.isUploadable
                    ? 'hover:bg-clay-50'
                    : 'opacity-50'
                }`}
              >
                {sf.isUploadable ? (
                  /* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */
                  <input
                    type="checkbox"
                    checked={sf.checked}
                    onChange={e => toggleStagedFile(sf.id, e.target.checked)}
                    className="w-4 h-4 rounded accent-green-600 cursor-pointer"
                  />
                ) : (
                  <div className="w-4 h-4" />
                )}
                <p className="text-xs text-ink-soft truncate" title={sf.file.name}>
                  {sf.file.name}
                </p>
                <Tag size="sm" tone={typeBadgeTone(sf.fileType)} className="w-full justify-center">
                  {sf.fileType}
                </Tag>
                <span className="text-xs text-ink-faint text-right">{sf.sizeStr}</span>
                <IconButton
                  icon={XCircle}
                  size="xs"
                  variant="ghost"
                  aria-label="Remove"
                  onClick={() => removeStagedFile(sf.id)}
                  className="text-ink-faint hover:bg-transparent hover:text-danger"
                />
              </div>
            ))}
          </div>

          {/* Footer */}
          {stagedOther.length > 0 && (
            <div className="px-5 py-2 border-t border-clay-100 bg-clay-50/80">
              <p className="text-xs text-clay-600">
                {stagedOther.length} non-PDF file{stagedOther.length !== 1 ? 's' : ''} (Excel, Word, images, etc.) are shown but cannot be processed — they will be skipped when you queue.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Unified upload tracker (drop → done, one row per file) ── */}
      {trackerTotal > 0 && (
        <div className="rounded-lg border border-border bg-cream-light overflow-hidden">

          {/* LlamaParse-style hero: pumpjack + one bar + one plain-English ETA */}
          <ExtractionProgress
            percent={overallPercent}
            active={isExtracting}
            total={trackerTotal}
            done={trackerCounts.done}
            attention={trackerCounts.attention}
            showDetails={showDetails}
            onToggleDetails={() => setShowDetails(v => !v)}
          />

          {/* Action row */}
          {(hasFinishedTrackerItems || failedJobs.length > 0) && (
            <div className="px-5 pb-3 flex items-center justify-end gap-3">
              {hasFinishedTrackerItems && (
                <Button variant="ghost" size="sm" onClick={clearFinished}>
                  Clear finished
                </Button>
              )}
              {failedJobs.length > 0 && (
                <Button
                  variant="danger-ghost"
                  size="sm"
                  icon={Trash2}
                  loading={deletingAll}
                  onClick={handleDeleteAllFailed}
                >
                  Clear all failed
                </Button>
              )}
            </div>
          )}

          {/* The tracker list — only attention items by default, all when expanded */}
          {visibleTrackerItems.length > 0 && (
          <div className="border-t border-border divide-y divide-border-subtle">
            {visibleTrackerItems.map((it) => {
              const job = it.job;
              const stuck = job && job.status === 'processing' && job.locked_at
                ? (Date.now() - new Date(job.locked_at).getTime()) > 3 * 60 * 1000
                : false;
              const showBar = it.stage === 'uploading' || it.stage === 'processing';
              const barColor = it.stage === 'uploading' ? 'bg-info' : 'bg-green-500';

              let icon: React.ReactNode;
              let label: string;
              let labelClass = 'text-ink-faint';
              switch (it.stage) {
                case 'queued':
                  icon = <Clock size={16} className="text-ink-faint" />;
                  label = 'Waiting'; break;
                case 'uploading':
                  icon = <Loader2 size={16} className="text-info animate-spin" />;
                  label = 'Uploading'; labelClass = 'text-info'; break;
                case 'review':
                  icon = <AlertTriangle size={16} className="text-clay-500" />;
                  label = 'Review before importing'; labelClass = 'text-clay-700'; break;
                case 'processing':
                  icon = <Loader2 size={16} className="text-green-600 animate-spin" />;
                  label = 'Reading statement'; labelClass = 'text-green-700'; break;
                case 'done':
                  icon = <CheckCircle size={16} className="text-green-600" />;
                  label = 'Done'; labelClass = 'text-green-700'; break;
                case 'needs_review':
                  icon = <AlertTriangle size={16} className="text-clay-500" />;
                  label = 'Imported — needs review'; labelClass = 'text-clay-700'; break;
                case 'skipped':
                  icon = <AlertTriangle size={16} className="text-clay-500" />;
                  label = 'Attention'; labelClass = 'text-clay-700'; break;
                case 'failed':
                default:
                  icon = <XCircle size={16} className="text-danger" />;
                  label = 'Needs attention'; labelClass = 'text-danger'; break;
              }

              return (
                <div key={it.key} className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex-shrink-0">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-900 truncate" title={it.filename}>
                        {it.filename}
                      </p>
                      {it.stage === 'processing' && job ? (
                        <JobProgressDetail jobId={job.id} status={job.status} fallbackMessage={job.progress_message} />
                      ) : (
                        <p className={`text-xs mt-1 ${labelClass}`}>
                          {label}
                          {it.stage === 'failed' && it.errorMessage ? ` — ${it.errorMessage}` : ''}
                          {it.stage === 'skipped' ? `: ${it.errorMessage ?? 'This exact file has already been uploaded for this case'}` : ''}
                          {it.stage === 'review' ? ' — choose which statements to import' : ''}
                        </p>
                      )}
                    </div>

                    {/* Per-row actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {it.stage === 'review' && it.entryId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const p = reviewBundles[it.entryId!];
                            if (p) { setPendingBundle(p); setPendingBundleEntryId(it.entryId!); }
                          }}
                        >
                          Open review
                        </Button>
                      )}
                      {stuck && job && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={Ban}
                          onClick={() => setForceResetTarget(job)}
                          title="This job has not updated in a few minutes — force it back to the queue"
                        >
                          Force reset
                        </Button>
                      )}
                      {it.stage === 'failed' && it.jobId != null && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={RotateCcw}
                            onClick={() => handleRetry(it.jobId!)}
                            loading={retryJob.isPending}
                          >
                            Retry
                          </Button>
                          <IconButton
                            icon={Trash2}
                            size="xs"
                            variant="ghost"
                            aria-label="Remove"
                            onClick={() => deleteJob.mutate(it.jobId!)}
                            disabled={deleteJob.isPending}
                            className="text-ink-faint hover:bg-transparent hover:text-danger"
                          />
                        </>
                      )}
                      {(it.stage === 'queued' || it.stage === 'skipped' || (it.stage === 'failed' && it.jobId == null)) && it.entryId && (
                        <IconButton
                          icon={XCircle}
                          size="xs"
                          variant="ghost"
                          aria-label="Dismiss"
                          onClick={() => removeEntry(it.entryId!)}
                          className="text-ink-faint hover:bg-transparent hover:text-danger"
                        />
                      )}
                    </div>
                  </div>

                  {showBar && (
                    <div className="mt-2 w-full bg-cream-dark rounded-full h-2 overflow-hidden">
                      <div
                        className={`${barColor} h-full rounded-full transition-[width] duration-500`}
                        style={{ width: `${Math.max(it.progress, 3)}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {listSource.length > TRACKER_VISIBLE_LIMIT && (
            <div className="px-5 py-3 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                icon={showAllTracker ? ChevronUp : ChevronDown}
                onClick={() => setShowAllTracker(!showAllTracker)}
              >
                {showAllTracker
                  ? 'Show fewer'
                  : `Show all ${listSource.length} files`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Upload history (modest, collapsed-by-default) ── */}
      {uploadHistory.length > 0 && (
        <div className="rounded-lg border border-border bg-cream-light/60 overflow-hidden">
          {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full px-5 py-3 flex items-center justify-between text-sm text-ink-soft hover:text-green-800 transition-colors"
          >
            <span className="inline-flex items-center gap-2 font-medium">
              <History size={15} className="text-ink-faint" />
              Upload history
              <span className="text-xs font-normal text-ink-faint">
                ({uploadHistory.length}{recentJobs.length > HISTORY_LIMIT ? '+' : ''})
              </span>
            </span>
            {showHistory ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {showHistory && (
            <div className="border-t border-border divide-y divide-border-subtle max-h-80 overflow-y-auto">
              {uploadHistory.map((row) => (
                <div key={row.key} className="px-5 py-2 flex items-center gap-3">
                  <Badge variant={jobStatusVariant(row.status)}>{jobStatusLabel(row.status)}</Badge>
                  <p className="text-xs text-ink-soft truncate flex-1" title={row.filename}>
                    {row.filename}
                  </p>
                  {row.isBundle && (
                    <span className="text-xs text-ink-faint flex-shrink-0">
                      {row.status === 'processing'
                        ? `${row.doneCount} of ${row.partTotal} statements done`
                        : `${row.partTotal} statement${row.partTotal === 1 ? '' : 's'}`}
                    </span>
                  )}
                  <span className="text-xs text-ink-faint flex-shrink-0">
                    {row.created_at ? formatUploadDate(row.created_at) : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Processed Statements ── */}
      <div>
        <h3 className="t-section text-green-900 mb-4">
          Processed Statements
          {!statementsLoading && statements.length > 0 && (
            <span className="text-sm font-normal text-ink-faint ml-2">
              {accountLabels.length} account{accountLabels.length !== 1 ? 's' : ''} &middot; {statements.length} statement{statements.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>

        {statementsLoading ? (
          <SkeletonTable rows={4} columns={3} />
        ) : statements.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-border-strong bg-cream-light">
            <EmptyState
              icon={FileText}
              title="No statements processed yet"
              description="Upload PDF bank statements above to get started"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedStatements).map(([accountLabel, stmts]) => {
              const isCollapsed = collapsedAccounts.has(accountLabel);
              const minDate = stmts[0]?.statement_start_date ?? null;
              const maxDate = stmts[stmts.length - 1]?.statement_end_date ?? null;
              // Reconciliation is silent when it succeeds — we only surface a
              // signal when there is a gap to look at.
              const smallGapCount = stmts.filter(s => {
                const st = s.reconciliation_status?.toLowerCase();
                return st === 'minor_gap' || st === 'gap' || st === 'close';
              }).length;
              const largeGapCount = stmts.filter(s =>
                s.reconciliation_status?.toLowerCase() === 'unreconciled'
              ).length;
              const gapLabel = (() => {
                const parts: string[] = [];
                if (largeGapCount > 0) parts.push(`${largeGapCount} need${largeGapCount === 1 ? 's' : ''} review`);
                if (smallGapCount > 0) parts.push(`${smallGapCount} small gap${smallGapCount === 1 ? '' : 's'}`);
                return parts.join(', ');
              })();

              return (
                <div key={accountLabel} className="rounded-lg overflow-hidden border border-border">
                  {/* Account header — always visible, clickable to toggle */}
                  {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
                  <button
                    onClick={() => toggleAccount(accountLabel)}
                    className="w-full bg-green-900 hover:bg-green-800 transition-colors text-white px-4 py-3 flex items-center gap-3 text-left"
                  >
                    {/* Account name */}
                    <span className="font-semibold text-sm flex-shrink-0">{accountLabel}</span>

                    {/* Date range */}
                    {(minDate || maxDate) && (
                      <span className="text-xs text-green-200 flex-shrink-0">
                        {stmts.length === 1
                          ? formatMonthYear(maxDate ?? minDate)
                          : formatRange(minDate, maxDate, formatMonthYear)}
                      </span>
                    )}

                    {/* Separator dot */}
                    <span className="text-green-400 text-xs flex-shrink-0">·</span>

                    {/* Statement count */}
                    <span className="text-xs text-green-200 flex-shrink-0">
                      {stmts.length} stmt{stmts.length !== 1 ? 's' : ''}
                    </span>

                    {/* Reconciliation signal — only shown when there is a gap */}
                    {gapLabel && (
                      <>
                        <span className="text-green-400 text-xs flex-shrink-0">·</span>
                        <span className={`text-xs font-medium flex-shrink-0 ${
                          largeGapCount > 0 ? 'text-danger-accent' : 'text-clay-300'
                        }`}>
                          {gapLabel}
                        </span>
                      </>
                    )}

                    {/* Spacer pushes remaining items to the right */}
                    <span className="flex-1" />

                    {/* Chevron — right side */}
                    <ChevronDown
                      size={16}
                      className={`flex-shrink-0 text-green-300 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
                    />
                  </button>

                  {/* Individual statement rows — shown when expanded */}
                  {!isCollapsed && (
                    <div className="divide-y divide-border-subtle">
                      {stmts.map((stmt) => {
                        const partyMismatch = getPartyMismatchWarning(stmt.parsing_warnings);
                        return (
                        <div key={stmt.id} className="bg-cream-light px-4 py-3 flex items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-green-900">
                                {formatRange(stmt.statement_start_date, stmt.statement_end_date, formatDate)}
                              </span>
                              <PartyBadge party={stmt.source_party} clientName={clientName} opposingPartyName={opposingPartyName} size="xs" />
                              {partyMismatch && (
                                <Tag size="sm" tone="warn" icon={AlertTriangle} title={partyMismatch.message}>
                                  {stmt.source_party
                                    ? `Conflict: PDF shows "${partyMismatch.holder_name}"`
                                    : `Undetected: "${partyMismatch.holder_name}" — assign manually`}
                                </Tag>
                              )}
                              {(() => {
                                const st = stmt.reconciliation_status?.toLowerCase();
                                const isSmallGap = st === 'close' || st === 'minor_gap' || st === 'gap';
                                const isLargeGap = st === 'unreconciled';
                                if (!isSmallGap && !isLargeGap) return null;
                                const amount = stmt.reconciliation_gap !== null && stmt.reconciliation_gap !== 0
                                  ? ` — ${money(stmt.reconciliation_gap)}`
                                  : '';
                                return (
                                  <Badge variant={isLargeGap ? 'danger' : 'warning'}>
                                    {isLargeGap ? 'Needs review' : 'Small gap'}{amount}
                                  </Badge>
                                );
                              })()}
                              {stmt.low_confidence_count > 0 && (
                                <span className="text-xs text-clay-500">
                                  {stmt.low_confidence_count} needs review
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-ink-faint flex-wrap">
                              <span>Balance: {money(stmt.ending_balance)}</span>
                              <span className="text-ink-faint">·</span>
                              <span className="truncate max-w-48" title={stmt.source_file}>
                                {stmt.source_file.split('/').pop() ?? stmt.source_file}
                              </span>
                              {stmt.total_cost_usd ? (
                                <>
                                  <span className="text-ink-faint">·</span>
                                  <span>${stmt.total_cost_usd.toFixed(4)} AI cost</span>
                                </>
                              ) : null}
                            </div>
                          </div>
                          <IconButton
                            icon={Trash2}
                            size="sm"
                            variant="ghost"
                            aria-label="Delete statement"
                            onClick={() => setDeleteTarget(stmt)}
                            className="text-ink-faint hover:bg-transparent hover:text-danger"
                          />
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteStatement}
        title="Delete Statement"
        message={`Are you sure you want to delete "${deleteTarget?.source_file}"? This will also remove all associated transactions.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteStatement.isPending}
      />

      <BundleReviewModal
        open={!!pendingBundle}
        bundleId={pendingBundle?.bundleId ?? null}
        filename={pendingBundle?.filename ?? ''}
        pageCount={pendingBundle?.pageCount ?? 0}
        initialBoundaries={pendingBundle?.boundaries ?? []}
        onClose={() => {
          // Keep the originating row visible as "Needs your review" so the user
          // can re-open the modal; only clear which modal is showing.
          setPendingBundle(null);
          setPendingBundleEntryId(null);
        }}
        onConfirmed={(result) => {
          // The bundle was split & queued. Keep the originating row visible with
          // no gap by linking it to the first child job (it tracks that job in
          // place); the remaining children appear as their own rows once polled.
          const ids = result.job_ids ?? [];
          rememberJobs(ids);
          if (pendingBundleEntryId) {
            const eid = pendingBundleEntryId;
            if (ids.length > 0) {
              setFiles(prev => prev.map(f =>
                f.id === eid ? { ...f, localStatus: 'submitted' as LocalFileStatus, jobId: ids[0] } : f
              ));
              setReviewBundles(prev => {
                if (!(eid in prev)) return prev;
                const next = { ...prev };
                delete next[eid];
                return next;
              });
            } else {
              removeEntry(eid);
            }
          }
          setPendingBundle(null);
          setPendingBundleEntryId(null);
          queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
          setToast(`Queued ${result.statements_created} statement${result.statements_created !== 1 ? 's' : ''} for processing.`);
        }}
      />

      <ConfirmModal
        open={!!forceResetTarget}
        onClose={() => setForceResetTarget(null)}
        onConfirm={async () => {
          if (!forceResetTarget) return;
          await forceResetJob.mutateAsync(forceResetTarget.id);
          setForceResetTarget(null);
          queryClient.invalidateQueries({ queryKey: ['caseJobs', caseId] });
        }}
        title="Force reset this job?"
        message={`"${forceResetTarget ? getJobFilename(forceResetTarget) : ''}" will be returned to the queue and started over from scratch. Any progress on the current attempt will be discarded. Use this when a job appears stuck and is not making progress.`}
        confirmLabel="Force reset"
        variant="danger"
        loading={forceResetJob.isPending}
      />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Per-job live progress detail (which page is being read). Polls every
// 2s while the job is processing. Falls back to the textual progress_message
// for jobs without per-page data (e.g. digital text-only PDFs).
// ──────────────────────────────────────────────────────────────────────────
function JobProgressDetail({ jobId, status, fallbackMessage }: {
  jobId: number;
  status: string;
  fallbackMessage: string | null;
}) {
  const isActive = status === 'processing' || status === 'pending';
  const { data } = useJobProgress(jobId, isActive);

  if (!data || data.pages_total === null) {
    if (fallbackMessage) {
      return <p className="text-xs text-ink-faint mt-1 truncate">{fallbackMessage}</p>;
    }
    return null;
  }

  return (
    <div className="mt-1 flex items-center gap-3 text-xs text-ink-faint flex-wrap">
      <span className="font-medium text-green-800">
        Reading page {data.pages_done} of {data.pages_total}
      </span>
      {data.pages_failed > 0 && (
        <>
          <span className="text-ink-faint">·</span>
          <span className="text-clay-600">{data.pages_failed} could not be read</span>
        </>
      )}
    </div>
  );
}
